import { apiClient } from '@/lib/api-client';
import type { ApiResponse, PaginatedResponse } from '@/types/api';

export interface ReportDto {
  id: string;
  reportType: string;
  format: string;
  status: string;
  fileUrl?: string;
  fileSizeBytes?: number;
  errorMessage?: string;
  createdAt: string;
  generatedAt?: string;
  expiresAt?: string;
}

export interface CreateReportRequest {
  reportType: string;
  format: string;
  propertyId?: string;
  deviceId?: string;
  startDate?: string;
  endDate?: string;
  deliveryEmail?: string;
  // Mirrors the property device list's sort. Forwarded to the backend
  // so the exported CSV/PDF rows match the order the user saw at the
  // moment of export. Allowed: sortBy "unit"|"createdAt", sortDir "asc"|"desc".
  sortBy?: string;
  sortDir?: string;
}

export interface ScheduledReportDto {
  id: string;
  reportType: string;
  format: string;
  propertyId?: string;
  frequency: string;
  deliveryEmail: string;
  // Custom email subject. Null/empty means "use the backend default
  // template" (e.g. "Your manor Usage Summary Report — Property Name").
  subject?: string | null;
  // Per-schedule sort + grouping. Allowed sortBy: "unit"|"serialNumber".
  // Allowed sortDir: "asc"|"desc". Allowed groupBy: "building"|null.
  // Null on any field = use the backend defaults (unit / asc / no
  // grouping). Schedules created before this feature shipped have
  // null values; the renderer applies the same defaults.
  sortBy?: string | null;
  sortDir?: string | null;
  groupBy?: string | null;
  isActive: boolean;
  lastRunAt?: string;
  nextRunAt?: string;
  createdAt: string;
}

export interface CreateScheduledReportRequest {
  reportType: string;
  format: string;
  propertyId?: string;
  frequency: string;
  deliveryEmail: string;
  // Optional custom subject. Send null (or omit) to let the backend
  // use its default template at send time.
  subject?: string | null;
  // See ScheduledReportDto.
  sortBy?: string | null;
  sortDir?: string | null;
  groupBy?: string | null;
}

export interface UpdateScheduledReportRequest {
  reportType: string;
  format: string;
  frequency: string;
  deliveryEmail: string;
  isActive: boolean;
  // See CreateScheduledReportRequest.subject.
  subject?: string | null;
  // null leaves the field untouched on the server. Send "" to clear
  // the user's selection (= fall back to defaults at render time).
  sortBy?: string | null;
  sortDir?: string | null;
  groupBy?: string | null;
}

export interface GetScheduledReportsParams {
  propertyId?: string;
  page?: number;
  perPage?: number;
}

// Reports
export async function createReport(data: CreateReportRequest): Promise<ReportDto> {
  const response = await apiClient.post<ReportDto>('/reports', data);
  return response.data;
}

export async function getReports(page = 1, perPage = 15): Promise<PaginatedResponse<ReportDto>> {
  const response = await apiClient.get<PaginatedResponse<ReportDto>>('/reports', { params: { page, perPage } });
  return response.data;
}

export async function getReport(id: string): Promise<ApiResponse<ReportDto>> {
  const response = await apiClient.get<ApiResponse<ReportDto>>(`/reports/${id}`);
  return response.data;
}

// Returns the blob alongside the filename advertised by the backend in
// `Content-Disposition`. Browsers can't read CD off a blob URL — so when
// we trigger an anchor click, the download falls back to a UUID-style
// name unless we forward the parsed filename via `a.download`.
// `ReportNaming.BuildFilename` (api/Norgas.Services/Helpers/ReportNaming.cs)
// produces `{propertySlug}-{reportType}-{date}.{ext}`.
export async function downloadReport(id: string): Promise<{ blob: Blob; filename: string | null }> {
  const response = await apiClient.get(`/reports/${id}/download`, { responseType: 'blob' });
  const disposition = response.headers?.['content-disposition'] as string | undefined;
  return { blob: response.data, filename: parseContentDispositionFilename(disposition) };
}

function parseContentDispositionFilename(disposition: string | undefined): string | null {
  if (!disposition) return null;
  // RFC 5987 `filename*=UTF-8''<percent-encoded>` takes precedence over
  // the unencoded `filename=` when both are present.
  const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch) {
    try {
      return decodeURIComponent(utfMatch[1].trim());
    } catch {
      /* fall through to the unencoded form */
    }
  }
  const quoted = disposition.match(/filename="([^"]+)"/i);
  if (quoted) return quoted[1];
  const bare = disposition.match(/filename=([^;]+)/i);
  if (bare) return bare[1].trim();
  return null;
}

export async function deleteReport(id: string): Promise<void> {
  await apiClient.delete(`/reports/${id}`);
}

// Scheduled Reports
export async function createScheduledReport(data: CreateScheduledReportRequest): Promise<ScheduledReportDto> {
  const response = await apiClient.post<ScheduledReportDto>('/scheduled-reports', data);
  return response.data;
}

export async function getScheduledReports(
  params: GetScheduledReportsParams = {},
): Promise<PaginatedResponse<ScheduledReportDto>> {
  const { propertyId, page = 1, perPage = 15 } = params;
  const response = await apiClient.get<PaginatedResponse<ScheduledReportDto>>('/scheduled-reports', {
    params: { propertyId, page, perPage },
  });
  return response.data;
}

export async function updateScheduledReport(
  id: string,
  data: UpdateScheduledReportRequest,
): Promise<ScheduledReportDto> {
  const response = await apiClient.put<ApiResponse<ScheduledReportDto>>(`/scheduled-reports/${id}`, data);
  return response.data.data!;
}

export async function deleteScheduledReport(id: string): Promise<void> {
  await apiClient.delete(`/scheduled-reports/${id}`);
}

export async function runScheduledReport(id: string): Promise<void> {
  await apiClient.post(`/scheduled-reports/${id}/run`);
}
